import React, { useEffect, useState, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/theme-variables.css';

// Context
import { AuthProvider } from './context/AuthContext';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Cart from './components/cart/Cart';
import SuspensionModal from './components/common/SuspensionModal';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoryPage from './pages/CategoryPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import WorkshopsPage from './pages/WorkshopsPage';
import WorkshopDetailPage from './pages/WorkshopDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import MyOrdersPage from './pages/MyOrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';

// Redux actions
import { fetchBanners } from './store/slices/bannerSlice';
import { fetchCategories } from './store/slices/categoriesSlice';
import { fetchSettings } from './store/slices/settingsSlice';
import { loadTenant } from './store/slices/tenantSlice';

// Utils
import { setupAxiosInterceptors, setSuspensionModalCallback } from './utils/axiosInterceptor';
import { selectModuleEnabled } from './store/slices/tenantSlice';
import axios from 'axios';
import { API_URL } from './config/api';

// Apply theme from backend on app load
const applyThemeFromBackend = async () => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const response = await axios.get(`${API_URL}/api/settings/theme`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        const theme = response.data.data;
        const root = document.documentElement;
        
        // Apply theme colors
        if (theme.config && theme.config.colors) {
          const colors = theme.config.colors;
          root.style.setProperty('--primary-color', colors.primary || '#D4AF37');
          root.style.setProperty('--secondary-color', colors.secondary || '#B8860B');
          root.style.setProperty('--accent-color', colors.accent || '#FF6B6B');
          root.style.setProperty('--background-color', colors.background || '#FEFEFE');
          root.style.setProperty('--surface-color', colors.surface || '#FFFFFF');
          root.style.setProperty('--text-color', colors.text || '#1A1A1A');
          root.style.setProperty('--text-secondary-color', colors.textSecondary || '#666666');
        }
      }
    }
  } catch (error) {
    console.error('Failed to load theme from backend:', error);
  }
};

// Component to protect routes based on module access
const ModuleProtectedRoute = ({ children, moduleCode }) => {
  const moduleEnabled = useSelector(selectModuleEnabled(moduleCode));
  
  if (!moduleEnabled) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [suspensionModal, setSuspensionModal] = useState({
    isOpen: false,
    status: null,
    message: '',
    endDate: null
  });

  useEffect(() => {
    // Setup axios interceptors
    setupAxiosInterceptors();
    
    // Apply theme from backend
    applyThemeFromBackend();
    
    // Set callback for suspension modal
    setSuspensionModalCallback((modalData) => {
      setSuspensionModal(modalData);
    });
    
    // Set callback for tenant slice to close modal on reactivation
    const { setSuspensionModalCallbackForTenant } = require('./store/slices/tenantSlice');
    setSuspensionModalCallbackForTenant((modalData) => {
      setSuspensionModal(modalData);
    });
    
    // Check for persisted suspension status on mount
    const { getSuspensionStatus } = require('./utils/axiosInterceptor');
    const persistedStatus = getSuspensionStatus();
    if (persistedStatus) {
      setSuspensionModal({
        isOpen: true,
        status: persistedStatus.status,
        message: persistedStatus.message,
        endDate: persistedStatus.endDate
      });
    }

    // Load tenant context first (detects from URL)
    dispatch(loadTenant()).then(() => {
      // Load initial data after tenant is loaded
      dispatch(fetchBanners('home'));
      dispatch(fetchCategories());
      dispatch(fetchSettings());
    });
  }, [dispatch]);

  // Check suspension status on route changes
  useEffect(() => {
    const { getSuspensionStatus } = require('./utils/axiosInterceptor');
    const persistedStatus = getSuspensionStatus();
    if (persistedStatus && !suspensionModal.isOpen) {
      setSuspensionModal({
        isOpen: true,
        status: persistedStatus.status,
        message: persistedStatus.message,
        endDate: persistedStatus.endDate
      });
    } else if (!persistedStatus && suspensionModal.isOpen) {
      // Clear modal if suspension status was cleared
      setSuspensionModal({
        isOpen: false,
        status: null,
        message: '',
        endDate: null
      });
    }
  }, [location.pathname]); // Re-check on route change

  const handleCloseSuspensionModal = () => {
    // Do nothing - modal should not be dismissible when suspended
    // Only clear when account is reactivated (handled by backend)
  };

  return (
    <AuthProvider>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/category/:id" element={<CategoryPage />} />
            <Route 
              path="/blog" 
              element={
                <ModuleProtectedRoute moduleCode="BLOGS">
                  <BlogPage />
                </ModuleProtectedRoute>
              } 
            />
            <Route 
              path="/blog/:id" 
              element={
                <ModuleProtectedRoute moduleCode="BLOGS">
                  <BlogDetailPage />
                </ModuleProtectedRoute>
              } 
            />
            <Route 
              path="/workshops" 
              element={
                <ModuleProtectedRoute moduleCode="WORKSHOPS">
                  <WorkshopsPage />
                </ModuleProtectedRoute>
              } 
            />
            <Route 
              path="/workshops/:id" 
              element={
                <ModuleProtectedRoute moduleCode="WORKSHOPS">
                  <WorkshopDetailPage />
                </ModuleProtectedRoute>
              } 
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/orders" element={<MyOrdersPage />} />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
          </Routes>
        </main>
        <Footer />
        <Cart />
        <SuspensionModal
          isOpen={suspensionModal.isOpen}
          status={suspensionModal.status}
          message={suspensionModal.message}
          endDate={suspensionModal.endDate}
          onClose={handleCloseSuspensionModal}
        />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </AuthProvider>
  );
}

export default App;

